import React, { useState, useEffect } from 'react';
import { Plus, Check, X, Brain, Calendar, Flag, Edit3, Trash2 } from 'lucide-react';
import { masterSupabase } from '../lib/supabase-master.js';

const ToDoSection = () => {
  const [todos, setTodos] = useState([]);
  const [newTodo, setNewTodo] = useState('');
  const [newCategory, setNewCategory] = useState('general');
  const [newPriority, setNewPriority] = useState(2);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  // Clara360-spezifische Kategorien
  const categories = [
    { value: 'wartung', label: '🏠 Wartung', color: 'bg-orange-100 text-orange-800' },
    { value: 'finanzen', label: '💰 Finanzen', color: 'bg-green-100 text-green-800' },
    { value: 'mieter', label: '👥 Mieter', color: 'bg-blue-100 text-blue-800' },
    { value: 'verwaltung', label: '📋 Verwaltung', color: 'bg-purple-100 text-purple-800' },
    { value: 'clara_ki', label: '🧠 Clara-KI', color: 'bg-indigo-100 text-indigo-800' },
    { value: 'analysen', label: '📊 Analysen', color: 'bg-cyan-100 text-cyan-800' },
    { value: 'general', label: '📝 Allgemein', color: 'bg-gray-100 text-gray-800' }
  ];

  const priorities = [
    { value: 1, label: 'Sehr hoch', color: 'text-red-600', icon: '🔴' },
    { value: 2, label: 'Hoch', color: 'text-orange-600', icon: '🟠' },
    { value: 3, label: 'Normal', color: 'text-yellow-600', icon: '🟡' },
    { value: 4, label: 'Niedrig', color: 'text-green-600', icon: '🟢' },
    { value: 5, label: 'Sehr niedrig', color: 'text-gray-600', icon: '⚪' }
  ];

  useEffect(() => {
    loadTodos();
  }, []);

  const loadTodos = async () => {
    try {
      const { data: { user } } = await masterSupabase.auth.getUser();
      if (!user) return;

      const { data, error } = await masterSupabase
        .from('todos')
        .select('*')
        .eq('user_id', user.id)
        .order('priority', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTodos(data || []);
    } catch (error) {
      console.error('❌ Fehler beim Laden der To-Dos:', error);
    } finally {
      setLoading(false);
    }
  };

  const addTodo = async () => {
    if (!newTodo.trim()) return;

    try {
      const { data: { user } } = await masterSupabase.auth.getUser();
      if (!user) return;

      const { data, error } = await masterSupabase
        .from('todos')
        .insert({
          title: newTodo.trim(),
          category: newCategory,
          priority: newPriority,
          user_id: user.id,
          status: 'open'
        })
        .select()
        .single();

      if (error) throw error;
      
      setTodos([data, ...todos]);
      setNewTodo('');
      setNewCategory('general');
      setNewPriority(2);
    } catch (error) {
      console.error('❌ Fehler beim Erstellen der Aufgabe:', error);
    }
  };

  const toggleTodo = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 'open' ? 'done' : 'open';
      
      const { error } = await masterSupabase
        .from('todos')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;
      
      setTodos(todos.map(todo => 
        todo.id === id ? { ...todo, status: newStatus } : todo
      ));
    } catch (error) {
      console.error('❌ Fehler beim Aktualisieren der Aufgabe:', error);
    }
  };

  const deleteTodo = async (id) => {
    try {
      const { error } = await masterSupabase
        .from('todos')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setTodos(todos.filter(todo => todo.id !== id));
    } catch (error) {
      console.error('❌ Fehler beim Löschen der Aufgabe:', error);
    }
  };

  const startEdit = (todo) => {
    setEditingId(todo.id);
    setEditText(todo.title);
  };

  const saveEdit = async (id) => {
    if (!editText.trim()) return;

    try {
      const { error } = await masterSupabase
        .from('todos')
        .update({ 
          title: editText.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;
      
      setTodos(todos.map(todo => 
        todo.id === id ? { ...todo, title: editText.trim() } : todo
      ));
      setEditingId(null);
      setEditText('');
    } catch (error) {
      console.error('❌ Fehler beim Bearbeiten der Aufgabe:', error);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const generateClaraToDoS = async () => {
    // Placeholder für Clara-KI Integration
    const claraSuggestions = [
      { title: 'Monatliche Mieteingangsanalyse durchführen', category: 'analysen', priority: 2 },
      { title: 'Wartungsanfragen der letzten Woche prüfen', category: 'wartung', priority: 3 },
      { title: 'Neue Eigentümer-Dokumente vervollständigen', category: 'verwaltung', priority: 2 }
    ];

    try {
      const { data: { user } } = await masterSupabase.auth.getUser();
      if (!user) return;

      for (const suggestion of claraSuggestions) {
        const { data, error } = await masterSupabase
          .from('todos')
          .insert({
            ...suggestion,
            user_id: user.id,
            created_by_clara: true,
            status: 'open'
          })
          .select()
          .single();

        if (!error && data) {
          setTodos(prev => [data, ...prev]);
        }
      }
    } catch (error) {
      console.error('❌ Fehler bei Clara-Vorschlägen:', error);
    }
  };

  const getCategoryStyle = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.color : 'bg-gray-100 text-gray-800';
  };

  const getPriorityInfo = (priority) => {
    return priorities.find(p => p.value === priority) || priorities[2];
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Check className="w-6 h-6 text-green-600" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              To-Do Liste
            </h2>
            <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-0.5 rounded-full">
              {todos.filter(t => t.status === 'open').length} offen
            </span>
          </div>
          <button
            onClick={generateClaraToDoS}
            className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Brain className="w-4 h-4" />
            Clara-Vorschläge
          </button>
        </div>
      </div>

      {/* Neue Aufgabe hinzufügen */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex gap-3">
          <input
            type="text"
            value={newTodo}
            onChange={(e) => setNewTodo(e.target.value)}
            placeholder="Neue Aufgabe hinzufügen..."
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            onKeyPress={(e) => e.key === 'Enter' && addTodo()}
          />
          <select
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            {categories.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
          <select
            value={newPriority}
            onChange={(e) => setNewPriority(parseInt(e.target.value))}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            {priorities.map(p => (
              <option key={p.value} value={p.value}>{p.icon} {p.label}</option>
            ))}
          </select>
          <button
            onClick={addTodo}
            disabled={!newTodo.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Hinzufügen
          </button>
        </div>
      </div>

      {/* To-Do Liste */}
      <div className="p-6">
        {todos.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Check className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Keine Aufgaben vorhanden</p>
            <p className="text-sm">Erstellen Sie Ihre erste Aufgabe oder lassen Sie Clara Vorschläge machen</p>
          </div>
        ) : (
          <div className="space-y-3">
            {todos.map(todo => {
              const priorityInfo = getPriorityInfo(todo.priority);
              const isEditing = editingId === todo.id;
              
              return (
                <div
                  key={todo.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                    todo.status === 'done' 
                      ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-75' 
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600'
                  }`}
                >
                  {/* Checkbox */}
                  <button
                    onClick={() => toggleTodo(todo.id, todo.status)}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      todo.status === 'done'
                        ? 'bg-green-500 border-green-500 text-white'
                        : 'border-gray-300 dark:border-gray-600 hover:border-green-400 dark:hover:border-green-500'
                    }`}
                  >
                    {todo.status === 'done' && <Check className="w-3 h-3" />}
                  </button>

                  {/* Priorität */}
                  <span className={`text-lg ${priorityInfo.color}`}>
                    {priorityInfo.icon}
                  </span>

                  {/* Titel */}
                  <div className="flex-1">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') saveEdit(todo.id);
                          if (e.key === 'Escape') cancelEdit();
                        }}
                        autoFocus
                      />
                    ) : (
                      <span
                        className={`${
                          todo.status === 'done' ? 'line-through text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'
                        }`}
                      >
                        {todo.title}
                      </span>
                    )}
                  </div>

                  {/* Kategorie */}
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryStyle(todo.category)}`}>
                    {categories.find(c => c.value === todo.category)?.label || todo.category}
                  </span>

                  {/* Clara-KI Badge */}
                  {todo.created_by_clara && (
                    <span className="flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs font-medium">
                      <Brain className="w-3 h-3" />
                      Clara
                    </span>
                  )}

                  {/* Aktionen */}
                  <div className="flex items-center gap-1">
                    {isEditing ? (
                      <>
                        <button
                          onClick={() => saveEdit(todo.id)}
                          className="p-1 text-green-600 hover:bg-green-100 rounded"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEdit(todo)}
                          className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteTodo(todo.id)}
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ToDoSection;

